import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  Tooltip as MuiTooltip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  Email as EmailIcon,
  Save as SaveIcon,
  Science as TestIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AttachFile as AttachFileIcon,
  Reply as ReplyIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Inbox as InboxIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from '../api/axios';

const EmailTab = ({ companyId, projectId, project, isActive }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    localPart: '',
    fullEmailAddress: '',
    emailDomain: '',
    saveSenderEmail: true,
    senderEmailVariable: 'senderEmail',
    allowedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx'],
    maxFileSize: 50 * 1024 * 1024,
    maxFilesPerEmail: 10,
    separateExecutionPerFile: false,
    autoReplySettings: {
      enabled: true,
      invalidFileFormat: true,
      successConfirmation: false,
      customMessages: {
        invalidFileFormat: '',
        successConfirmation: '',
        processingError: ''
      }
    },
    security: {
      allowedSenderDomains: [],
      requireSubjectKeywords: [],
      blockAttachmentsWithoutText: false
    },
    whitelist: {
      useWhitelist: false,
      whitelistedEmails: []
    },
    statistics: {
      totalEmailsReceived: 0,
      totalExecutionsStarted: 0,
      totalInvalidFiles: 0,
      totalAutoRepliesSent: 0,
      totalWhitelistBlocked: 0,
      lastEmailReceivedAt: null,
      lastExecutionStartedAt: null,
      lastWhitelistBlockedAt: null
    }
  });
  
  // Test dialog state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testResults, setTestResults] = useState(null);
  
  // Statistics state
  const [statistics, setStatistics] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);

  // Received emails modal state
  const [receivedEmailsModalOpen, setReceivedEmailsModalOpen] = useState(false);
  const [receivedEmails, setReceivedEmails] = useState([]);
  const [receivedEmailsLoading, setReceivedEmailsLoading] = useState(false);
  const [receivedEmailsPage, setReceivedEmailsPage] = useState(0);
  const [receivedEmailsLimit] = useState(10);
  const [receivedEmailsTotal, setReceivedEmailsTotal] = useState(0);
  const [receivedEmailsSearch, setReceivedEmailsSearch] = useState('');

  // Fetch email settings when tab becomes active
  useEffect(() => {
    if (isActive) {
      fetchEmailSettings();
      fetchStatistics();
    }
  }, [isActive, companyId, projectId]);

  const fetchEmailSettings = async () => {
    try {
      setLoading(true);
      console.log('📧 Fetching email settings for project:', projectId);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/email-settings`);
      setEmailSettings(response.data);
      console.log('✅ Email settings loaded:', response.data);
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
      setError('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setStatisticsLoading(true);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/email-statistics`);
      setStatistics(response.data);
      console.log('📊 Email statistics loaded:', response.data);
    } catch (error) {
      console.error('Failed to fetch email statistics:', error);
      // Don't show error for statistics, it's not critical
    } finally {
      setStatisticsLoading(false);
    }
  };

  const fetchReceivedEmails = async (page = 0, search = '') => {
    try {
      setReceivedEmailsLoading(true);
      const params = { page: page + 1, limit: receivedEmailsLimit };
      if (search.trim()) params.search = search.trim();
      const response = await api.get(`/email/${companyId}/projects/${projectId}/received-emails`, { params });
      setReceivedEmails(response.data.emails);
      setReceivedEmailsTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch received emails:', error);
      setReceivedEmails([]);
      setReceivedEmailsTotal(0);
    } finally {
      setReceivedEmailsLoading(false);
    }
  };

  const handleReceivedEmailsModalOpen = () => {
    setReceivedEmailsModalOpen(true);
    setReceivedEmailsPage(0);
    fetchReceivedEmails(0, receivedEmailsSearch);
  };

  const handleReceivedEmailsModalClose = () => {
    setReceivedEmailsModalOpen(false);
  };

  const handleReceivedEmailsPageChange = (event, newPage) => {
    setReceivedEmailsPage(newPage);
    fetchReceivedEmails(newPage, receivedEmailsSearch);
  };

  const handleReceivedEmailsSearch = () => {
    setReceivedEmailsPage(0);
    fetchReceivedEmails(0, receivedEmailsSearch);
  };

  const saveEmailSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      console.log('💾 Saving email settings:', emailSettings);
      const response = await api.put(`/companies/${companyId}/projects/${projectId}/email-settings`, emailSettings);
      setEmailSettings(response.data);
      setSuccess('Email settings saved successfully!');
      
      // Refresh statistics after saving
      fetchStatistics();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to save email settings:', error);
      setError(error.response?.data?.message || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const testEmailConfiguration = async (testType = 'validation') => {
    try {
      setTesting(true);
      setError('');
      
      const requestData = { testType };
      if (testType === 'send-test-email' && testEmail) {
        requestData.testEmail = testEmail;
      }
      
      console.log('🧪 Testing email configuration:', requestData);
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/email-settings/test`, requestData);
      setTestResults(response.data);
      
      if (response.data.success) {
        setSuccess('Email configuration test completed successfully!');
      } else {
        setError('Email configuration test failed. Check the results below.');
      }
      
      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      
    } catch (error) {
      console.error('Email configuration test failed:', error);
      setError(error.response?.data?.message || 'Failed to test email configuration');
      setTestResults({
        success: false,
        error: error.response?.data?.message || error.message
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSettingChange = (path, value) => {
    setEmailSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess('Copied to clipboard!');
      setTimeout(() => setSuccess(''), 2000);
    }).catch(() => {
      setError('Failed to copy to clipboard');
      setTimeout(() => setError(''), 2000);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTestResultIcon = (passed) => {
    if (passed === true) return <CheckCircleIcon color="success" />;
    if (passed === false) return <ErrorIcon color="error" />;
    return <InfoIcon color="info" />;
  };

  if (!isActive) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
        <Box ml={2}>
          <Typography variant="body2" color="textSecondary">
            Loading email settings...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Email Execution Settings
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={4}>
        Configure email-triggered workflow executions. Users can send emails with attachments to automatically start processing.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Main Enable/Disable Toggle */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, mb: 3, border: emailSettings.enabled ? '2px solid #4caf50' : 'none' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <EmailIcon color={emailSettings.enabled ? 'success' : 'disabled'} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Email Execution System
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Allow users to start workflow executions by sending emails
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={emailSettings.enabled}
                onChange={(e) => handleSettingChange('enabled', e.target.checked)}
                size="large"
                color="success"
              />
            </Box>
            
            {emailSettings.enabled && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Email execution is active. Configure the settings below to customize the behavior.
                </Typography>
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Email Address Configuration */}
        {emailSettings.enabled && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Email Address Configuration
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Local Part"
                    value={emailSettings.localPart}
                    onChange={(e) => handleSettingChange('localPart', e.target.value)}
                    placeholder="project-name"
                    helperText="Choose a unique identifier for your project email address"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          @{emailSettings.emailDomain}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      Full Email Address
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip 
                        label={emailSettings.fullEmailAddress || `${emailSettings.localPart || 'your-project'}@${emailSettings.emailDomain}`}
                        color={emailSettings.fullEmailAddress ? 'success' : 'default'}
                        icon={<EmailIcon />}
                      />
                      {emailSettings.fullEmailAddress && (
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(emailSettings.fullEmailAddress)}
                          title="Copy email address"
                        >
                          <CopyIcon />
                        </IconButton>
                      )}
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      Users will send emails to this address to trigger executions
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={emailSettings.saveSenderEmail}
                        onChange={(e) => handleSettingChange('saveSenderEmail', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Save sender email address as variable"
                  />
                </Grid>
                
                {emailSettings.saveSenderEmail && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Sender Email Variable Name"
                      value={emailSettings.senderEmailVariable}
                      onChange={(e) => handleSettingChange('senderEmailVariable', e.target.value)}
                      placeholder="senderEmail"
                      helperText="Variable name to store the sender's email address"
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* File Settings */}
        {emailSettings.enabled && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                <AttachFileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                File Attachment Settings
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Allowed File Types</InputLabel>
                    <Select
                      multiple
                      value={emailSettings.allowedFileTypes}
                      onChange={(e) => handleSettingChange('allowedFileTypes', e.target.value)}
                      label="Allowed File Types"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value.toUpperCase()} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {['pdf', 'png', 'jpg', 'jpeg', 'gif', 'xlsx', 'xls', 'docx', 'doc', 'txt', 'csv'].map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Select which file types users can send via email</FormHelperText>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max File Size (MB)"
                    value={Math.round(emailSettings.maxFileSize / (1024 * 1024))}
                    onChange={(e) => handleSettingChange('maxFileSize', parseInt(e.target.value) * 1024 * 1024)}
                    inputProps={{ min: 1, max: 100 }}
                    helperText={`Current: ${formatFileSize(emailSettings.maxFileSize)}`}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Files Per Email"
                    value={emailSettings.maxFilesPerEmail}
                    onChange={(e) => handleSettingChange('maxFilesPerEmail', parseInt(e.target.value))}
                    inputProps={{ min: 1, max: 20 }}
                    helperText="Maximum attachments per email"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailSettings.separateExecutionPerFile || false}
                          onChange={(e) => handleSettingChange('separateExecutionPerFile', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            Process each file as separate execution
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            When enabled, each file attachment will start its own execution instead of processing all files together
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', ml: 0 }}
                    />
                    {emailSettings.separateExecutionPerFile && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Example:</strong> If an email contains 3 PDF files, the system will create 3 separate executions - one for each file.
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Auto-Reply Settings */}
        {emailSettings.enabled && (
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ReplyIcon />
                  <Typography variant="h6" fontWeight="medium">
                    Auto-Reply Settings
                  </Typography>
                  {emailSettings.autoReplySettings.enabled && (
                    <Chip label="Enabled" color="success" size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailSettings.autoReplySettings.enabled}
                          onChange={(e) => handleSettingChange('autoReplySettings.enabled', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Enable automatic email replies"
                    />
                  </Grid>
                  
                  {emailSettings.autoReplySettings.enabled && (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={emailSettings.autoReplySettings.invalidFileFormat}
                              onChange={(e) => handleSettingChange('autoReplySettings.invalidFileFormat', e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Send reply for invalid file formats"
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={emailSettings.autoReplySettings.successConfirmation}
                              onChange={(e) => handleSettingChange('autoReplySettings.successConfirmation', e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Send confirmation when processing starts"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Invalid File Format Message"
                          value={emailSettings.autoReplySettings.customMessages.invalidFileFormat}
                          onChange={(e) => handleSettingChange('autoReplySettings.customMessages.invalidFileFormat', e.target.value)}
                          placeholder="Thank you for your email. Unfortunately, the file format you sent is not supported..."
                          helperText="Use {allowedTypes} to insert the list of allowed file types"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Success Confirmation Message"
                          value={emailSettings.autoReplySettings.customMessages.successConfirmation}
                          onChange={(e) => handleSettingChange('autoReplySettings.customMessages.successConfirmation', e.target.value)}
                          placeholder="Thank you for your email. We have received your files and started processing them..."
                          helperText="Message sent when processing starts successfully"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}

        {/* Email Whitelist Settings */}
        {emailSettings.enabled && (
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SecurityIcon />
                  <Typography variant="h6" fontWeight="medium">
                    Email Whitelist
                  </Typography>
                  {emailSettings.whitelist?.useWhitelist && (
                    <Chip 
                      label={`${emailSettings.whitelist?.whitelistedEmails?.length || 0} emails`} 
                      color="primary" 
                      size="small" 
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailSettings.whitelist?.useWhitelist || false}
                          onChange={(e) => handleSettingChange('whitelist.useWhitelist', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Use Email Whitelist"
                    />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      When enabled, only emails from whitelisted addresses will trigger executions
                    </Typography>
                  </Grid>
                  
                  {emailSettings.whitelist?.useWhitelist && (
                    <>
                      <Grid item xs={12}>
                        <WhitelistEmailManager
                          whitelistedEmails={emailSettings.whitelist?.whitelistedEmails || []}
                          onChange={(emails) => handleSettingChange('whitelist.whitelistedEmails', emails)}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Security Note:</strong> Only emails from the addresses listed above will be processed. 
                            All other emails will be ignored silently.
                          </Typography>
                        </Alert>
                      </Grid>
                    </>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}

        {/* Statistics */}
        {emailSettings.enabled && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="bold">
                  <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Email Statistics
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={fetchStatistics}
                  disabled={statisticsLoading}
                  size="small"
                >
                  {statisticsLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
              </Box>
              
              {statistics ? (
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 1 }}>
                        <EmailIcon />
                      </Avatar>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {statistics.totalEmailsReceived}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Emails Received
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1 }}>
                        <CheckCircleIcon />
                      </Avatar>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {statistics.totalExecutionsStarted}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Executions Started
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 1 }}>
                        <WarningIcon />
                      </Avatar>
                      <Typography variant="h4" fontWeight="bold" color="warning.main">
                        {statistics.totalInvalidFiles}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Invalid Files
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Avatar sx={{ bgcolor: 'error.main', mx: 'auto', mb: 1 }}>
                        <SecurityIcon />
                      </Avatar>
                      <Typography variant="h4" fontWeight="bold" color="error.main">
                        {statistics.totalWhitelistBlocked || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Blocked Emails
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 1 }}>
                        <ReplyIcon />
                      </Avatar>
                      <Typography variant="h4" fontWeight="bold" color="info.main">
                        {statistics.successRate}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Success Rate
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  No statistics available yet. Statistics will appear after receiving emails.
                </Typography>
              )}
            </Paper>
          </Grid>
        )}

        {/* Test Configuration */}
        {emailSettings.enabled && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                <TestIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Test Configuration
              </Typography>
              
              <Box display="flex" gap={2} mb={3}>
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={20} /> : <TestIcon />}
                  onClick={() => testEmailConfiguration('validation')}
                  disabled={testing || !emailSettings.localPart}
                >
                  {testing ? 'Testing...' : 'Test Configuration'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => setTestDialogOpen(true)}
                  disabled={testing || !emailSettings.localPart}
                >
                  Send Test Email
                </Button>
              </Box>
              
              {testResults && (
                <Alert severity={testResults.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Test Results: {testResults.success ? 'Passed' : 'Failed'}
                  </Typography>
                  
                  {testResults.tests && (
                    <List dense>
                      {Object.entries(testResults.tests).map(([testName, result]) => (
                        <ListItem key={testName} disablePadding>
                          <ListItemIcon>
                            {getTestResultIcon(result.passed)}
                          </ListItemIcon>
                          <ListItemText
                            primary={testName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            secondary={result.error || result.message || (result.passed ? 'Passed' : 'Failed')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                  
                  {testResults.error && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      Error: {testResults.error}
                    </Typography>
                  )}
                </Alert>
              )}
            </Paper>
          </Grid>
        )}

        {/* Save Button and View Received Emails */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Button
              variant="outlined"
              startIcon={<InboxIcon />}
              onClick={handleReceivedEmailsModalOpen}
              disabled={loading}
            >
              View Received Emails
            </Button>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={fetchEmailSettings}
                disabled={loading || saving}
              >
                Reset Changes
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={saveEmailSettings}
                disabled={loading || saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Received Emails Modal */}
      <Dialog
        open={receivedEmailsModalOpen}
        onClose={handleReceivedEmailsModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <InboxIcon color="primary" />
            Received Emails
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by sender email address..."
            value={receivedEmailsSearch}
            onChange={(e) => setReceivedEmailsSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleReceivedEmailsSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleReceivedEmailsSearch}>Search</Button>
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />
          {receivedEmailsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date/Time</strong></TableCell>
                    <TableCell><strong>From</strong></TableCell>
                    <TableCell><strong>Subject</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receivedEmails.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="textSecondary">
                          No received emails found. Emails are retained for 7 days.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    receivedEmails.map((email) => (
                      <TableRow key={email._id}>
                        <TableCell>
                          {email.receivedAt ? new Date(email.receivedAt).toLocaleString() : '-'}
                        </TableCell>
                        <TableCell>{email.emailFrom || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {email.emailSubject || '-'}
                        </TableCell>
                        <TableCell>
                          {email.whitelistInfo?.useWhitelist && !email.whitelistInfo?.isWhitelisted ? (
                            <Chip label="Not on Whitelist" color="warning" size="small" />
                          ) : email.status === 'processed' ? (
                            <Chip label="Processed" color="success" size="small" />
                          ) : email.status === 'whitelist_blocked' ? (
                            <Chip label="Not on Whitelist" color="warning" size="small" />
                          ) : email.status === 'disabled' ? (
                            <Chip label="Disabled" color="default" size="small" />
                          ) : (
                            <Chip label={email.status || 'Unknown'} size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {receivedEmailsTotal > 0 && (
            <TablePagination
              component="div"
              count={receivedEmailsTotal}
              page={receivedEmailsPage}
              onPageChange={handleReceivedEmailsPageChange}
              rowsPerPage={receivedEmailsLimit}
              rowsPerPageOptions={[receivedEmailsLimit]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReceivedEmailsModalClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <SendIcon color="primary" />
            Send Test Email
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Send a test email to verify your configuration is working correctly.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Test Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="your-email@example.com"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              testEmailConfiguration('send-test-email');
              setTestDialogOpen(false);
            }}
            variant="contained"
            disabled={!testEmail || testing}
            startIcon={testing ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {testing ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// WhitelistEmailManager Component
const WhitelistEmailManager = ({ whitelistedEmails, onChange }) => {
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addEmail = () => {
    const trimmedEmail = newEmail.trim().toLowerCase();
    
    if (!trimmedEmail) {
      setEmailError('Please enter an email address');
      return;
    }
    
    if (!isValidEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (whitelistedEmails.includes(trimmedEmail)) {
      setEmailError('This email address is already in the whitelist');
      return;
    }
    
    onChange([...whitelistedEmails, trimmedEmail]);
    setNewEmail('');
    setEmailError('');
  };

  const removeEmail = (emailToRemove) => {
    onChange(whitelistedEmails.filter(email => email !== emailToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight="bold" mb={2}>
        Whitelisted Email Addresses ({whitelistedEmails.length})
      </Typography>
      
      {/* Add new email input */}
      <Box display="flex" gap={1} mb={3}>
        <TextField
          fullWidth
          label="Add Email Address"
          value={newEmail}
          onChange={(e) => {
            setNewEmail(e.target.value);
            setEmailError('');
          }}
          onKeyPress={handleKeyPress}
          placeholder="user@example.com"
          error={!!emailError}
          helperText={emailError || 'Enter email address and press Enter or click Add'}
          size="small"
        />
        <Button
          variant="contained"
          onClick={addEmail}
          disabled={!newEmail.trim()}
          sx={{ minWidth: 80 }}
        >
          Add
        </Button>
      </Box>
      
      {/* List of whitelisted emails */}
      {whitelistedEmails.length > 0 ? (
        <Box>
          <Typography variant="body2" color="textSecondary" mb={1}>
            Whitelisted Email Addresses:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {whitelistedEmails.map((email, index) => (
              <Chip
                key={index}
                label={email}
                onDelete={() => removeEmail(email)}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Alert severity="warning">
          <Typography variant="body2">
            No email addresses whitelisted. All emails will be ignored until you add at least one email address.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default EmailTab;
